import type React from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import type {
  BookedFlightItineraryWithDepartureTime,
  DisruptionProtectionAncillaryContract,
  MissedConnection,
} from "@b2bportal/air-booking-api";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getFlightsRebookRoutes } from "~core-flights-rebook/navigationFlow";

// Redefined to avoid dependency on `core-trips`
export type TripsAction = {
  label: React.ReactNode;
  action: () => void;
};

export const useRebookFlightTripAction = (
  itinerary: BookedFlightItineraryWithDepartureTime
): TripsAction | null => {
  const navigate = useNavigate();
  const { FULL_ROUTES } = getFlightsRebookRoutes();

  const { t } = useI18nContext();

  const { disruptionProtection, missedConnection } = itinerary.ancillaries;

  if (
    disruptionProtection != null &&
    isEligibleForRebook(disruptionProtection)
  ) {
    const params = new URLSearchParams({
      contractId: disruptionProtection.id,
    });
    return {
      label: t("core-flights.rebook.trips.rebook-flight"),
      action: () => navigate(`${FULL_ROUTES.SUMMARY}?${params.toString()}`),
    };
  }

  if (missedConnection != null && isEligibleForRebook(missedConnection)) {
    const params = new URLSearchParams({
      contractId: missedConnection.id.productId,
    });
    return {
      label: t("core-flights.rebook.trips.rebook-flight"),
      action: () => navigate(`${FULL_ROUTES.SUMMARY}?${params.toString()}`),
    };
  }

  return null;
};

const isEligibleForRebook = (
  protection: DisruptionProtectionAncillaryContract | MissedConnection
): boolean => {
  switch (protection.status.DisruptionContractStatus) {
    case "AnyExerciseEligible":
    case "Eligible":
    case "MarkedEligible":
    case "RebookOnlyEligible":
      return true;

    case "AlreadyExercised":
    case "Cancel":
    case "Conclude":
    case "ExerciseNotEligible":
    case "Purchase":
    case "Rebook":
    case "Redeem":
    case "Refund":
    case "Remove":
      return false;

    default:
      return ensureExhaustive(protection.status, false);
  }
};
